import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import Highlighter from "react-highlight-words";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { useTheme, Box, Stack } from "@mui/material";
import EzTextStyle from "../EzTextStyle/EzTextStyle.es.js";
import useScrollIntoView from "../../utils/hooks/useScrollIntoView.es.js";
import EzIcon from "../EzIcon/EzIcon.es.js";
import { useId } from "@radix-ui/react-id";
const getItemId = (listId, itemKey) => `${listId}-option-${itemKey}`;
const EzListBoxGroup = ({ groupName, children }) => {
  const id = useId();
  const theme = useTheme();
  return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(Box, { "aria-describedby": id, component: "ul", role: "group", sx: { listStyle: "none", m: 0, p: 0 }, children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "li",
        id,
        role: "presentation",
        sx: {
          color: theme.tokens["color-text-subtle"],
          fontSize: theme.tokens["typography-fontsize-50"],
          fontWeight: theme.tokens["typography-fontweight-semibold"],
          lineHeight: theme.tokens["typography-lineheight-dense"],
          px: theme.tokens["spacing-4"],
          py: theme.tokens["spacing-1"]
        },
        children: groupName
      }
    ),
    children
  ] }) });
};
const EzListBox = forwardRef(function EzListBox2({ collection, focusLabel, id, searchWords, selectionManager, ...props }, ref) {
  const theme = useTheme();
  const activeOptionRef = useRef();
  const { items } = collection;
  const hasParentChildOptions = items.find(({ isParent }) => isParent);
  const hasGroupedOptions = (options) => Array.isArray(options[0]);
  useScrollIntoView({ containerRef: ref, targetRef: activeOptionRef }, [selectionManager.focusedKey]);
  const renderItem = ({ description, disabled, key, label, parentValue }) => {
    const isCurrent = selectionManager.selectedKey === key;
    const isSelected = selectionManager.focusedKey === key;
    const shouldReceiveFocus = focusLabel === label && selectionManager.selectedKey === null;
    return /* @__PURE__ */ jsxs(
      Stack,
      {
        "aria-current": isCurrent,
        "aria-selected": isSelected,
        className: "EzListBox",
        component: "li",
        direction: "row",
        id: getItemId(id, key),
        onClick: () => selectionManager.replaceSelection(key),
        onMouseOver: () => selectionManager.setFocusedKey(key),
        ref: isSelected || shouldReceiveFocus ? activeOptionRef : void 0,
        role: "option",
        sx: {
          alignItems: "center",
          borderRadius: theme.tokens["radius-default"],
          color: theme.tokens["color-text-default"],
          cursor: "pointer",
          justifyContent: "space-between",
          opacity: disabled && 0.6,
          px: theme.tokens["spacing-4"],
          py: theme.tokens["spacing-2"],
          marginBottom: theme.tokens["spacing-2"],
          pointerEvents: disabled && "none",
          "&:hover": {
            bgcolor: theme.tokens["color-surface-hovered"]
          }
        },
        children: [
          /* @__PURE__ */ jsxs(Stack, { ml: hasParentChildOptions && parentValue ? theme.tokens["spacing-3"] : 0, children: [
            /* @__PURE__ */ jsx(
              Highlighter,
              {
                searchWords: searchWords || [],
                textToHighlight: label,
                highlightStyle: {
                  backgroundColor: "inherit",
                  color: "currentColor",
                  textDecoration: disabled ? void 0 : "underline"
                }
              }
            ),
            description && /* @__PURE__ */ jsx(EzTextStyle, { use: "subdued", children: description })
          ] }),
          isCurrent && /* @__PURE__ */ jsx(Stack, { alignItems: "center", ml: theme.tokens["spacing-3"], children: /* @__PURE__ */ jsx(EzIcon, { icon: faCheck }) })
        ]
      },
      label
    );
  };
  const renderGroup = ([name, options]) => /* @__PURE__ */ jsx(EzListBoxGroup, { groupName: name, children: options.map(renderItem) }, name);
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "ul",
      id,
      ref,
      role: "listbox",
      sx: {
        bgcolor: theme.tokens["color-surface-default"],
        borderRadius: theme.tokens["radius-default"],
        boxShadow: theme.tokens["shadow-md"],
        listStyle: "none",
        maxHeight: "20rem",
        overflow: "scroll",
        p: theme.tokens["spacing-05"],
        mt: theme.tokens["spacing-2"]
      },
      ...props,
      children: /* @__PURE__ */ jsx(Box, { m: theme.tokens["spacing-2"], children: hasGroupedOptions(items) ? items.map(renderGroup) : items.map(renderItem) })
    }
  );
});
export {
  EzListBox as default,
  getItemId
};
//# sourceMappingURL=EzListBox.es.js.map
